// Pure parsers for prescription dosage/duration text — no React, no path aliases (unit-testable).

export type ParsedDosage = {
  unit: 'ml' | 'unit';          // 'unit' = tablets/capsules/sachets (bare numbers)
  maxPerDose: number;           // largest single dose, e.g. 1 in "1-0-½"
  perDay: number;               // total per day, e.g. 1.5 in "1-0-½"
  dosesPerDay: number;          // number of non-zero doses per day
  sos: boolean;
};

const FRACTIONS: Record<string, number> = { '¼': 0.25, '½': 0.5, '¾': 0.75 };

// "1½" → 1.5, "½" → 0.5, "2.5" → 2.5, "" → NaN
export function parseAmount(text: string): number {
  const t = text.trim();
  const m = t.match(/^(\d+(?:\.\d+)?)?\s*([¼½¾])?$/);
  if (!m || (!m[1] && !m[2])) return NaN;
  return (m[1] ? parseFloat(m[1]) : 0) + (m[2] ? FRACTIONS[m[2]] : 0);
}

// Parses the dosage patterns the prescription form uses:
// "3.5ml-3.5ml-3.5ml", "½-0-½", "1-1-1-1", "5ml SOS", "5ml every 4 hrs", "2 tabs-0-2 tabs".
// Returns null for anything non-numeric ("SOS" alone, "As directed", drops, puffs…).
export function parseDosagePattern(dosage: string | null | undefined): ParsedDosage | null {
  const raw = (dosage ?? '').trim().toLowerCase();
  if (!raw) return null;
  if (/drop|puff|unit|iu\b|apply|as directed|as per/.test(raw)) return null;
  const unit: ParsedDosage['unit'] = raw.includes('ml') ? 'ml' : 'unit';
  const strip = (p: string) => p.replace(/ml|tabs?|tablets?|caps?|capsules?|sachets?/g, '').trim();

  const every = raw.match(/^(.+?)\s*(?:every|q)\s*(\d+)\s*(?:h|hr|hrs|hours?)\b/);
  if (every) {
    const amt = parseAmount(strip(every[1]));
    const hours = parseInt(every[2]);
    if (!(amt > 0) || !(hours > 0)) return null;
    const n = Math.floor(24 / hours);
    return { unit, maxPerDose: amt, perDay: amt * n, dosesPerDay: n, sos: false };
  }
  const sos = raw.match(/^(.+?)\s*(?:sos|prn|as needed)$/);
  if (sos) {
    const amt = parseAmount(strip(sos[1]));
    if (!(amt > 0)) return null;
    // SOS totals assume at most 4 doses in 24 h
    return { unit, maxPerDose: amt, perDay: amt * 4, dosesPerDay: 4, sos: true };
  }

  const parts = raw.split('-').map(p => parseAmount(strip(p) || '0'));
  if (parts.length < 2 || parts.length > 6 || parts.some(p => isNaN(p))) return null;
  const doses = parts.filter(p => p > 0);
  if (!doses.length) return null;
  return {
    unit,
    maxPerDose: Math.max(...doses),
    perDay: doses.reduce((a, b) => a + b, 0),
    dosesPerDay: doses.length,
    sos: false,
  };
}

// "5 days" → 5, "2 weeks" → 14, "1 month" → 30; the non-numeric defaults mirror the form's quantity logic
export function parseDurationDays(duration: string | null | undefined): number {
  const d = (duration ?? '').trim();
  const dl = d.toLowerCase();
  if (!dl) return 0;
  if (dl.includes('day'))   { const m = d.match(/(\d+)\s*days?/i);   return m ? parseInt(m[1]) : 0; }
  if (dl.includes('week'))  { const m = d.match(/(\d+)\s*weeks?/i);  return m ? parseInt(m[1]) * 7 : 0; }
  if (dl.includes('month')) { const m = d.match(/(\d+)\s*months?/i); return m ? parseInt(m[1]) * 30 : 0; }
  if (dl.includes('continue') || dl.includes('long term')) return 30;
  if (dl.includes('till review') || dl.includes('till next visit')) return 15;
  if (dl.includes('complete course')) return 7;
  if (dl.includes('as directed')) return 10;
  const m = d.match(/^\d+/);
  return m ? parseInt(m[0]) : 0;
}

// Bottle volume from catalog pack size text, e.g. "60 ml", "Bottle of 100ml"
export function parsePackVolumeMl(packSize: string | null | undefined): number | null {
  const m = (packSize ?? '').match(/(\d+(?:\.\d+)?)\s*ml/i);
  return m ? parseFloat(m[1]) : null;
}
